import { useTranslation } from "react-i18next";
import { MdArrowBackIos } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "../../components/GeneralComponents/Button";
import GeneralErrorState from "../../components/GeneralComponents/GeneralErrorState";
import LanguageSwitcher from "../../components/GeneralComponents/LanguageSwitcher";
import Loader from "../../components/GeneralComponents/Loader";
import usePaginatedFetchData from "../../hooks/usePaginatedFetchData";
import { setRegisterForm } from "../../redux/slices/generalSlice";
import { RootState } from "../../redux/store";
import { APISQUERYKEYS } from "../../utils/api/apisQueryKeys";
import { EndPoints } from "../../utils/api/endPoints";

const PrivacyPolicyRoute: React.FC = () => {
  const { t } = useTranslation("translation");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { registerForm } = useSelector((state: RootState) => state.general);
  const handleButtonNavigate = (path: string) => {
    navigate(path);
    dispatch(
      setRegisterForm({ phoneNumber: "", username: "", privacy: false }),
    );
  };
  const handleAgree = () => {
    navigate("/auth/register");
    dispatch(setRegisterForm({ ...registerForm, privacy: true }));
  };
  /**
   * Get user information from Redux store
   */
  const { user } = useSelector((state: RootState) => state.auth);

  /**
   * Fetch the privacy policy data using custom hook
   * Retrieves the privacy policy content and handles loading and error states
   */
  const {
    data: ManagementData,
    isLoading: ManagementLoading,
    error: ManagementError,
  } = usePaginatedFetchData({
    queryKey: APISQUERYKEYS.MANAGEMENT,
    endpoint: EndPoints.settingsManagementManagement,
    token: user?.token,
  });
  /**
   * Get privacy policy content from the response
   */
  let PP: any = ManagementData?.Data.PrivacyPolicy;

  /**
   * Loading state
   * If the data is still being fetched, display a loader
   */
  if (ManagementLoading) {
    return (
      <div className="flex flex-col items-center justify-center">
        <Loader radius={50} />
      </div>
    );
  }

  /**
   * Error state
   * If there is an error while fetching data, display a general error state
   */
  if (ManagementError) {
    return <GeneralErrorState />;
  }

  /**
   * Render the privacy policy content
   * Once data is fetched successfully, render it inside a styled container
   */
  return (
    <div className="relative flex flex-col gap-10 m-4 min-h-[calc(100vh-2rem)] overflow-hidden rounded-lg bg-primaryMain p-4 md:p-6">
      {/* Header Section */}
      <div className="flex w-full items-center justify-between">
        <LanguageSwitcher />
        <div className="mt-3 flex">
          <Button
            title={t("back to domecare")}
            variant="outlined"
            icon={<MdArrowBackIos />}
            onClick={() => handleButtonNavigate("/auth/login")}
          />
        </div>
      </div>

      {/* Main Content Card */}
      <div className="flex justify-center px-4">
        <div className="h-[60vh] w-full max-w-[1300px] overflow-y-auto rounded-2xl bg-white p-6 text-lg text-bodyTextColor shadow-card sm:h-[650px] md:h-[70vh] xl:mt-20 xl:h-[700px] 2xl:mt-0 2xl:h-[800px]">
          <div dangerouslySetInnerHTML={{ __html: PP }}></div>
          <div className="w-1/4 justify-self-end mt-10">
            <Button
              title={t("i agree")}
              variant="primary"
              onClick={() => handleAgree()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyRoute;
